import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { t } from "i18next";
import { Cue } from "../../shared/interfaces/GameLogicTypes";
import { renderCueContent } from "../../shared/helper/renderCueContent";
import VersusLaserRing, { LaserRingPlayer, LaserTarget } from "./VersusLaserRing";
import useUiScale, { useFitScale } from "../../shared/helper/useUiScale";
import "./SequenceComparison.css";

// Cue colours, indexed by cueNumber-1. Mirrors getColorStylebyNumber / the theme palette.
const CUE_HEX = ["#fff355", "#608ad6", "#e75151", "#92dc67", "#e17d47"];
const CUE_FG = ["#333333", "#ffffff", "#ffffff", "#ffffff", "#ffffff"];
const RANK_COLORS = ["#FFD700", "#B0B0B0", "#CD7F32", "#555555", "#c0392b"];
const RANK_STROKES = ["#8a7100", "#505050", "#7a4c1e", "#222222", "#7a1010"];

const LABEL_H_MOBILE = 44; // label (2 lines) + breathing room before the sequence
const LABEL_H_DESKTOP = 38;
// The desktop panel's natural height at scale 1 (stage + score area) and what else the results
// page needs beside it (question header, paper padding, the progression button). Drives useFitScale.
const DESKTOP_NATURAL_H = 420;
const DESKTOP_RESERVED_H = 250;

// Stage timeline (ms from mount)
const T_SETTLE = 1400; // left sequence flies from hero -> resting
const T_RIGHT = 2100; // right sequence pops in
const T_LINES = 2900; // connecting lines draw one by one
const T_SCORE = 3700; // score counts up

interface Dims {
  barH: number;
  maxFontPx: number;
  minFontPx: number;
  gap: number;
  stride: number;
  seqH: number;
  colWidth: string;
  rotate: number; // resting rotation in degrees (0 on mobile)
  mobile: boolean;
  rankFont: number; // rem
  heroScale: number;
  heroShift: string;
}

// Auto-fit text: shrinks the font until the full answer fits the bar.
// NEVER truncates / ellipsizes — long answers just get a smaller font.
function FitText({
  text,
  maxPx,
  minPx,
  height,
}: {
  text: string;
  maxPx: number;
  minPx: number;
  height: number;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const [fontPx, setFontPx] = useState(maxPx);

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const fit = () => {
      let size = maxPx;
      el.style.fontSize = size + "px";
      let guard = 0;
      while (
        (el.scrollHeight > el.clientHeight + 0.5 ||
          el.scrollWidth > el.clientWidth + 0.5) &&
        size > minPx &&
        guard < 80
      ) {
        size -= 0.5;
        el.style.fontSize = size + "px";
        guard++;
      }
      setFontPx(size);
    };
    fit();
    const ro = new ResizeObserver(fit);
    ro.observe(el);
    return () => ro.disconnect();
  }, [text, maxPx, minPx, height]);

  return (
    <div ref={ref} className="sc-bar-text" style={{ fontSize: fontPx, height }}>
      {renderCueContent(text)}
    </div>
  );
}

export interface SequenceComparisonProps {
  leftLabel: string;
  rightLabel: string;
  leftSequence: number[]; // truth, cue numbers 1-5
  rightSequence: number[]; // guess, cue numbers 1-5
  score: number;
  cues: Cue[];
  heading?: string; // e.g. "Jordan's prediction" — makes clear whose score this is
  caption?: string; // tier sentence — what the score means
  mobile?: boolean;
  // STANDARD_VERSUS: player avatars that ring the left (group) sequence and fire lasers into it
  // during the hero "pop", then retract. Omit for co-op.
  laserPlayers?: LaserRingPlayer[];
}

// A single horizontal coloured bar positioned by its rank within a sequence.
function Bar({
  cueNumber,
  rank,
  cues,
  dims,
}: {
  cueNumber: number;
  rank: number;
  cues: Cue[];
  dims: Dims;
}) {
  const idx = cueNumber - 1;
  const text = cues[idx] ? String(cues[idx].content) : "";
  return (
    <div
      className="sc-bar sc-bar-pop"
      style={{
        top: rank * dims.stride,
        height: dims.barH,
        backgroundColor: CUE_HEX[idx] ?? "#bbb",
        animationDelay: `${rank * 0.13}s`,
      }}
    >
      <span
        className="sc-rank"
        style={{
          color: RANK_COLORS[rank],
          WebkitTextStroke: `1px ${RANK_STROKES[rank]}`,
          fontSize: `${dims.rankFont}rem`,
        }}
      >
        {rank + 1}
      </span>
      <div style={{ flex: 1, minWidth: 0, color: CUE_FG[idx] ?? "#fff" }}>
        <FitText text={text} maxPx={dims.maxFontPx} minPx={dims.minFontPx} height={dims.barH - 6} />
      </div>
    </div>
  );
}

function Sequence({
  sequence,
  cues,
  dims,
  render,
}: {
  sequence: number[];
  cues: Cue[];
  dims: Dims;
  render: boolean;
}) {
  return (
    <div className="sc-seq" style={{ height: dims.seqH }}>
      {render &&
        sequence.map((cueNumber, rank) => (
          <Bar key={cueNumber} cueNumber={cueNumber} rank={rank} cues={cues} dims={dims} />
        ))}
    </div>
  );
}

// SVG bezier connector. y for cue c = centre of its bar as a fraction of seqH.
function Connector({
  leftSequence,
  rightSequence,
  dims,
  drawn,
}: {
  leftSequence: number[];
  rightSequence: number[];
  dims: Dims;
  drawn: boolean;
}) {
  const y = (rank: number) => ((rank * dims.stride + dims.barH / 2) / dims.seqH) * 100;
  return (
    <svg
      width="100%"
      height="100%"
      viewBox="0 0 100 100"
      preserveAspectRatio="none"
      style={{ position: "absolute", inset: 0, display: "block" }}
    >
      {leftSequence.map((cueNumber, lRank) => {
        const rRank = rightSequence.indexOf(cueNumber);
        if (rRank === -1) return null;
        const l = y(lRank);
        const r = y(rRank);
        const matches = lRank === rRank;
        return (
          <path
            key={cueNumber}
            d={`M 0 ${l} C 50 ${l} 50 ${r} 100 ${r}`}
            fill="none"
            pathLength={100}
            stroke={CUE_HEX[cueNumber - 1] ?? "#bbb"}
            strokeWidth={2.2}
            strokeOpacity={matches ? 0.85 : 0.55}
            className="sc-line"
            style={{
              strokeDashoffset: drawn ? 0 : 100,
              transitionDelay: `${lRank * 0.12}s`,
            }}
          />
        );
      })}
    </svg>
  );
}

export default function SequenceComparison(props: SequenceComparisonProps) {
  const mobile = !!props.mobile;
  // Everything here is authored against 1080p and multiplied up on large displays (index.css);
  // on a viewport shorter than that, useFitScale shrinks it back down so it still fits.
  const uiScale = useUiScale();
  const fit = useFitScale(DESKTOP_NATURAL_H, DESKTOP_RESERVED_H);
  const scale = mobile ? uiScale : uiScale * fit;
  const px = (v: number) => Math.round(v * scale);
  const LABEL_H = mobile ? LABEL_H_MOBILE : LABEL_H_DESKTOP;
  const barH = px(mobile ? 46 : 43);
  const gap = px(mobile ? 14 : 10);
  const stride = barH + gap;
  const dims: Dims = {
    barH,
    maxFontPx: px(mobile ? 17 : 15.5),
    minFontPx: px(7),
    gap,
    stride,
    seqH: 5 * stride - gap,
    colWidth: mobile ? "44%" : "42%",
    rotate: mobile ? 0 : 5,
    mobile,
    rankFont: (mobile ? 1.4 : 1.25) * (mobile ? 1 : fit),
    heroScale: mobile ? 1.22 : 1.15,
    heroShift: mobile ? "60%" : "66%",
  };
  // Reserved layout height for the resting sequences. The hero zoom is allowed
  // to overflow this (containers must NOT clip it — see .sc-stage / Paper).
  const stageH = dims.seqH + px(LABEL_H) + px(6);
  // Sequences tilt outward at the top, so nudge each label outward to sit over it.
  const labelShift = px(mobile ? 0 : 14);
  // The label box + its gap must add up to exactly LABEL_H, or the connector (which is offset by
  // LABEL_H) stops lining up with the bars.
  const labelGap = px(mobile ? 14 : 12);
  const labelBox = px(LABEL_H) - labelGap;
  // Everything the stylesheet sizes in rem follows the same factor.
  const cssVars = {
    ["--sc-scale" as any]: mobile ? 1 : fit,
    ["--sc-label-box" as any]: `${labelBox}px`,
    ["--sc-label-gap" as any]: `${labelGap}px`,
  };

  // stage: 0 hero, 1 settled, 2 right shown, 3 lines drawn, 4 score
  const [stage, setStage] = useState(0);
  const [displayScore, setDisplayScore] = useState(0);
  const rafRef = useRef<number | null>(null);

  // Versus: measure the group (left) sequence's on-screen box so the lasers aim at it exactly.
  const leftSeqRef = useRef<HTMLDivElement>(null);
  const [laserTarget, setLaserTarget] = useState<LaserTarget | null>(null);
  // Versus laser choreography: avatars fly in → beams fire & meet → sequence pops → lasers leave.
  const isVersus = !!(props.laserPlayers && props.laserPlayers.length > 0);
  const [laserActive, setLaserActive] = useState(false); // avatars present
  const [laserFiring, setLaserFiring] = useState(false);  // beams firing
  const [seqRevealed, setSeqRevealed] = useState(!isVersus); // group sequence shown (coop: immediately)
  useLayoutEffect(() => {
    if (!props.laserPlayers || props.laserPlayers.length === 0) return;
    const measure = () => {
      const el = leftSeqRef.current;
      if (!el) return;
      const r = el.getBoundingClientRect();
      setLaserTarget({ x: r.left + r.width / 2, y: r.top + r.height / 2, rx: r.width / 2, ry: r.height / 2 });
    };
    measure();
    const t = setTimeout(measure, 120);
    window.addEventListener("resize", measure);
    return () => { clearTimeout(t); window.removeEventListener("resize", measure); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mobile, props.laserPlayers?.length]);

  useEffect(() => {
    setStage(0);
    setDisplayScore(0);
    const timers: ReturnType<typeof setTimeout>[] = [];
    if (isVersus) {
      // Avatars fly in → beams fire & meet → sequence pops → lasers fade FULLY out → avatars move
      // out → sequence settles into the comparison.
      const FIRE = 700;         // beams start (after the avatars have flown in)
      const MEET = 1250;        // beams have converged → reveal the sequence
      const BEAMS_OUT = 2200;   // lasers fade out first
      const AVATARS_OUT = 2800; // then the avatars move back out (~600ms after the beams are gone)
      const SETTLE = 3400;      // then the sequence settles
      const EXTRA = SETTLE - T_SETTLE;
      setLaserActive(true);
      setLaserFiring(false);
      setSeqRevealed(false);
      timers.push(setTimeout(() => setLaserFiring(true), FIRE));
      timers.push(setTimeout(() => setSeqRevealed(true), MEET));
      timers.push(setTimeout(() => setLaserFiring(false), BEAMS_OUT));
      timers.push(setTimeout(() => setLaserActive(false), AVATARS_OUT));
      timers.push(setTimeout(() => setStage(1), T_SETTLE + EXTRA));
      timers.push(setTimeout(() => setStage(2), T_RIGHT + EXTRA));
      timers.push(setTimeout(() => setStage(3), T_LINES + EXTRA));
      timers.push(setTimeout(() => setStage(4), T_SCORE + EXTRA));
    } else {
      timers.push(setTimeout(() => setStage(1), T_SETTLE));
      timers.push(setTimeout(() => setStage(2), T_RIGHT));
      timers.push(setTimeout(() => setStage(3), T_LINES));
      timers.push(setTimeout(() => setStage(4), T_SCORE));
    }
    return () => {
      timers.forEach(clearTimeout);
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.leftSequence, props.rightSequence, props.score]);

  // Count the score up once we reach the final stage.
  useEffect(() => {
    if (stage < 4) return;
    const duration = 1000;
    const target = props.score;
    const start = performance.now();
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    const tick = (now: number) => {
      const p = Math.min((now - start) / duration, 1);
      const eased = 1 - Math.pow(1 - p, 3);
      setDisplayScore(Math.round(eased * target));
      if (p < 1) rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stage]);

  const heroMode = stage === 0;

  return (
    <div className="sc-root" style={cssVars}>
      {/* In versus, lift the sequence stage above the beam layer (z 1200) so the lasers end BEHIND
          the ranking bars while still showing over the panel background. */}
      <div className="sc-stage" style={{ height: stageH, zIndex: props.laserPlayers && props.laserPlayers.length > 0 ? 1300 : undefined }}>
        {/* Versus: lasers ring the group ranking during its hero pop, then retract. */}
        {props.laserPlayers && props.laserPlayers.length > 0 && (
          <VersusLaserRing
            players={props.laserPlayers}
            active={laserActive}
            firing={laserFiring}
            target={laserTarget}
            isMobile={mobile}
          />
        )}
        {/* Left (truth) column */}
        <div className="sc-col" style={{ width: dims.colWidth }}>
          <p
            className="sc-label"
            style={{ opacity: stage >= 1 ? 1 : 0, transform: `translateX(${-labelShift}px)` }}
          >
            {props.leftLabel.toUpperCase()}
          </p>
          <div
            ref={leftSeqRef}
            className="sc-seq-wrap"
            style={{
              transform: heroMode
                ? `translateX(${dims.heroShift}) scale(${dims.heroScale})`
                : `rotate(-${dims.rotate}deg)`,
              transformOrigin: "center",
              // Versus: keep it laid out (so the lasers can be aimed at it) but hidden until the
              // beams have met in the middle, then it pops in. Keep the CSS transform transition
              // (the fluent hero → side motion) and only add opacity on top of it.
              opacity: seqRevealed ? 1 : 0,
              transition: "transform 0.75s cubic-bezier(0.34, 1.2, 0.64, 1), opacity 0.25s ease-out",
            }}
          >
            {/* Re-key on reveal so the bars replay their pop animation when they appear. */}
            <Sequence key={isVersus ? String(seqRevealed) : "c"} sequence={props.leftSequence} cues={props.cues} dims={dims} render />
          </div>
        </div>

        {/* Connector */}
        <div className="sc-connector" style={{ opacity: stage >= 2 ? 1 : 0 }}>
          <div
            className="sc-connector-inner"
            style={{ top: px(LABEL_H), height: dims.seqH }}
          >
            <Connector
              leftSequence={props.leftSequence}
              rightSequence={props.rightSequence}
              dims={dims}
              drawn={stage >= 3}
            />
          </div>
        </div>

        {/* Right (guess) column */}
        <div
          className="sc-col"
          style={{ width: dims.colWidth, opacity: stage >= 2 ? 1 : 0 }}
        >
          <p className="sc-label" style={{ transform: `translateX(${labelShift}px)` }}>
            {props.rightLabel.toUpperCase()}
          </p>
          <div
            className="sc-seq-wrap"
            style={{ transform: `rotate(${dims.rotate}deg)`, transformOrigin: "center" }}
          >
            <Sequence
              sequence={props.rightSequence}
              cues={props.cues}
              dims={dims}
              render={stage >= 2}
            />
          </div>
        </div>
      </div>

      {/* Score + meaning */}
      <div className="sc-score-area" style={{ opacity: stage >= 4 ? 1 : 0 }}>
        {props.heading && <div className="sc-score-head">{props.heading}</div>}
        <div className="sc-score">{displayScore.toLocaleString()}</div>
        <div className="sc-score-sub">{t("out_of_2000")}</div>
        {props.caption && <div className="sc-score-tier">{props.caption}</div>}
      </div>
    </div>
  );
}
